/**
 * Limit copy — the ONE description of every plan/usage limit a user can hit.
 *
 * What a limit is called, the period it covers, what counts toward it and what
 * does not are written here once and nowhere else. The backend builds its
 * PLAN_LIMIT / ORG_PLAN_LIMIT / USAGE_BLOCKED messages from it, and the web app
 * imports this same file (alias `@beheard/limits`, see web/vite.config.ts) for
 * its pricing, usage and upgrade copy.
 *
 * Numbers are NOT here: they are passed in from the plan definitions the
 * enforcers read (config/planPolicies.ts, config/orgPlanPolicies.ts,
 * types/usage.ts) or from an API response. Each description below states the
 * rule the enforcer actually applies:
 *
 *   PlanLimitService (services/PlanLimitService.ts, sessionStartPlanLimit.ts)
 *     - "per day" means the last 24 hours (rolling), "per month" the calendar
 *       month so far. Every practice-cap path and the usage display read ONE
 *       count (services/plan/practiceStartCounts.ts): the user's
 *       `practice_sessions` rows plus canonical scenario starts plus legacy
 *       coaching conversations on a scenario, so a discarded or unfinished
 *       session still counts and the shown "used" is the enforced "used".
 *     - A practice-session start is checked against the practice-session
 *       limits; an NPC battle start is checked ONLY against the NPC limit
 *       (maxDailyNpcBattles), and NPC battles are never part of the
 *       practice-session totals.
 *   Conversation start admission (services/conversation/sessionAdmission.ts),
 *   when the credit reservation is off
 *     - An authored-scenario or NPC-level conversation is a practice session /
 *       NPC battle under the SAME limits and counts (legacy rows plus these
 *       starts). Open coaching, Interview Prep, Language and Song practice are
 *       coaching conversations and do not count — as the copy below says.
 *   UsageService (services/UsageService.ts), Free plan only
 *     - "per day" is the calendar day (from midnight, server time), "per month"
 *       the calendar month. Only recorded uses count (an action is recorded
 *       when it is admitted and metered; a released reservation is removed).
 *
 * This file must stay dependency-free: the browser bundle compiles it too.
 */

/** The window a limit is counted over. */
export type LimitPeriod = 'rolling_24h' | 'calendar_day' | 'calendar_month' | 'standing';

/** Which enforcer refuses an action at this limit ('none' = defined but not enforced anywhere). */
export type LimitEnforcer = 'PlanLimitService' | 'none';

export interface LimitCopy {
  /** Title-case name, e.g. "Practice sessions". */
  title: string;
  /** Lower-case plural noun used inside sentences, e.g. "practice sessions". */
  noun: string;
  period: LimitPeriod;
  scope: 'user' | 'org';
  enforcedBy: LimitEnforcer;
  /** Full sentence: what counts toward the limit. */
  counts?: string;
  /** Full sentence: what does NOT count (or is not blocked by it). */
  doesNotCount?: string;
  /** A separately enforced limit that is easy to confuse with this one. */
  separateLimit?: PlanLimitKey;
}

/** Limit keys of the plan definitions (planPolicies.ts PlanLimits + orgPlanPolicies.ts OrgPlanLimits + add-ons). */
export type PlanLimitKey =
  | 'maxDailyPracticeSessions'
  | 'maxMonthlyPracticeSessions'
  | 'maxDailyNpcBattles'
  | 'maxMonthlyNpcBattles'
  | 'maxDailyAiVoiceMinutes'
  | 'maxMonthlyAiVoiceMinutes'
  | 'maxContentSets'
  | 'maxContentItemsPerSet'
  | 'maxBlueprints'
  | 'maxPetals'
  | 'maxPortfolioItems'
  | 'maxMonthlyCoachingSessions'
  | 'maxRagDocuments'
  | 'maxRagStorageMb'
  | 'maxCoachingClients'
  | 'maxActiveEngagements'
  | 'maxSavedCandidates'
  | 'maxMonthlyContactRequests'
  | 'maxEvaluations'
  | 'maxMembers'
  | 'maxTrainingModules'
  | 'maxAssignmentsPerMember'
  | 'maxModuleLessons'
  | 'maxSharedContentSets'
  | 'maxSharedContentItemsPerSet'
  | 'maxOrgRagDocuments'
  | 'maxOrgRagStorageMb';

const PRACTICE_COUNTS =
  'Every practice session you start counts: AI Voice Coach, scenario and training-assignment sessions, including sessions you discard or leave unfinished.';
const PRACTICE_DOES_NOT_COUNT =
  "NPC Practice battles, Free Practice and Private Knowledge recordings, Coaching conversations and assessments don't count.";

/** Every plan limit key → its copy. */
export const LIMIT_COPY: Record<PlanLimitKey, LimitCopy> = {
  // ── Enforced by PlanLimitService ─────────────────────────────────────────
  maxDailyPracticeSessions: {
    title: 'Practice sessions',
    noun: 'practice sessions',
    period: 'rolling_24h',
    scope: 'user',
    enforcedBy: 'PlanLimitService',
    counts: PRACTICE_COUNTS,
    doesNotCount: PRACTICE_DOES_NOT_COUNT,
    separateLimit: 'maxDailyNpcBattles',
  },
  maxMonthlyPracticeSessions: {
    title: 'Practice sessions',
    noun: 'practice sessions',
    period: 'calendar_month',
    scope: 'user',
    enforcedBy: 'PlanLimitService',
    counts: PRACTICE_COUNTS,
    doesNotCount: PRACTICE_DOES_NOT_COUNT,
    separateLimit: 'maxDailyNpcBattles',
  },
  maxDailyNpcBattles: {
    title: 'NPC battles',
    noun: 'NPC battles',
    period: 'rolling_24h',
    scope: 'user',
    enforcedBy: 'PlanLimitService',
    counts: 'Every NPC Practice battle you start counts, including battles you discard or leave unfinished.',
    doesNotCount:
      "Other practice sessions don't use it up, and NPC battles don't count toward your practice-session limit.",
    separateLimit: 'maxDailyPracticeSessions',
  },
  maxTrainingModules: {
    title: 'Active training modules',
    noun: 'active training modules',
    period: 'standing',
    scope: 'org',
    enforcedBy: 'PlanLimitService',
    doesNotCount: "Archived modules don't count.",
  },
  maxAssignmentsPerMember: {
    title: 'Training assignments per member',
    noun: 'training assignments per member',
    period: 'standing',
    scope: 'org',
    enforcedBy: 'PlanLimitService',
    counts: 'Counts every training assignment the member has in this organization, completed ones included.',
  },

  maxContentSets: {
    title: 'Content sets',
    noun: 'content sets',
    period: 'standing',
    scope: 'user',
    enforcedBy: 'PlanLimitService',
    counts: 'Counts the content sets that exist for the owner (you, or your workspace); deleting one frees a place.',
  },

  // ── Defined in the plan tables but not enforced anywhere today ───────────
  // Labelled so an API that lists them reads well; never presented as a rule.
  maxMonthlyNpcBattles: { title: 'NPC battles', noun: 'NPC battles', period: 'calendar_month', scope: 'user', enforcedBy: 'none' },
  maxDailyAiVoiceMinutes: { title: 'AI voice minutes', noun: 'AI voice minutes', period: 'rolling_24h', scope: 'user', enforcedBy: 'none' },
  maxMonthlyAiVoiceMinutes: { title: 'AI voice minutes', noun: 'AI voice minutes', period: 'calendar_month', scope: 'user', enforcedBy: 'none' },
  maxContentItemsPerSet: { title: 'Items per content set', noun: 'items per content set', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxBlueprints: { title: 'Blueprints', noun: 'blueprints', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxPetals: { title: 'Petals', noun: 'petals', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxPortfolioItems: { title: 'Portfolio items', noun: 'portfolio items', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxMonthlyCoachingSessions: { title: 'Coaching sessions', noun: 'coaching sessions', period: 'calendar_month', scope: 'user', enforcedBy: 'none' },
  maxRagDocuments: { title: 'Knowledge base documents', noun: 'knowledge base documents', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxRagStorageMb: { title: 'Knowledge base storage (MB)', noun: 'MB of knowledge base storage', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxCoachingClients: { title: 'Coaching clients', noun: 'coaching clients', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxActiveEngagements: { title: 'Active engagements', noun: 'active engagements', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxSavedCandidates: { title: 'Saved candidates', noun: 'saved candidates', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxMonthlyContactRequests: { title: 'Contact requests', noun: 'contact requests', period: 'calendar_month', scope: 'user', enforcedBy: 'none' },
  maxEvaluations: { title: 'Evaluations', noun: 'evaluations', period: 'standing', scope: 'user', enforcedBy: 'none' },
  maxMembers: { title: 'Team members', noun: 'team members', period: 'standing', scope: 'org', enforcedBy: 'none' },
  maxModuleLessons: { title: 'Lessons per module', noun: 'lessons per module', period: 'standing', scope: 'org', enforcedBy: 'none' },
  maxSharedContentSets: { title: 'Shared content sets', noun: 'shared content sets', period: 'standing', scope: 'org', enforcedBy: 'none' },
  maxSharedContentItemsPerSet: { title: 'Items per shared content set', noun: 'items per shared content set', period: 'standing', scope: 'org', enforcedBy: 'none' },
  maxOrgRagDocuments: { title: 'Organization knowledge base documents', noun: 'organization knowledge base documents', period: 'standing', scope: 'org', enforcedBy: 'none' },
  maxOrgRagStorageMb: { title: 'Organization knowledge base storage (MB)', noun: 'MB of organization knowledge base storage', period: 'standing', scope: 'org', enforcedBy: 'none' },
};

/**
 * Free-plan per-action limits enforced by UsageService (types/usage.ts
 * USAGE_ALLOWANCE_RULES freeTierLimitPerDay / freeTierLimitPerMonth), by action.
 */
export interface UsageActionCopy {
  title: string;
  noun: string;
  counts: string;
  doesNotCount?: string;
}

export const USAGE_ACTION_COPY: Record<string, UsageActionCopy> = {
  interview_prep_extraction: {
    title: 'Interview preparations',
    noun: 'interview preparations',
    counts: 'Each job description read by the AI to prepare an interview practice counts.',
    doesNotCount: "Preparations that could not be read don't count.",
  },
  voice_practice_classic: {
    title: 'Voice analyses',
    noun: 'voice analyses',
    counts: 'Each recorded answer that is transcribed and scored counts, in any practice mode.',
    doesNotCount: "Typed answers don't count.",
  },
  voice_practice_live_minute: {
    title: 'Live voice practice minutes',
    noun: 'live voice practice minutes',
    counts: 'Each minute of live voice practice counts.',
  },
  tts_coach_response: {
    title: 'Spoken coach replies',
    noun: 'spoken coach replies',
    counts: 'Each coach reply read aloud counts.',
  },
  listen_full_pitch: {
    title: 'Full pitch listens',
    noun: 'full pitch listens',
    counts: 'Each full pitch you play counts.',
    doesNotCount: "Previews don't count.",
  },
  exam_attempt: { title: 'Exam attempts', noun: 'exam attempts', counts: 'Each exam you start counts.' },
  evaluate_candidate: { title: 'Candidate evaluations', noun: 'candidate evaluations', counts: 'Each candidate evaluation counts.' },
  contact_candidate: { title: 'Contact requests', noun: 'contact requests', counts: 'Each contact request you send counts.' },
  content_upload: { title: 'Content uploads', noun: 'content uploads', counts: 'Each file or link you upload counts.' },
  content_query: {
    title: 'Content lookups',
    noun: 'content lookups',
    counts: 'Each time practice feedback looks up your uploaded content counts.',
  },
};

// ── Phrasing ────────────────────────────────────────────────────────────────

/** "per day (rolling 24 hours)" — the rate phrase after a number. Empty for standing limits. */
export function periodRate(period: LimitPeriod): string {
  switch (period) {
    case 'rolling_24h':
      return 'per day (rolling 24 hours)';
    case 'calendar_day':
      return 'per day (resets at midnight, server time)';
    case 'calendar_month':
      return 'per calendar month';
    case 'standing':
      return '';
  }
}

/** "in the last 24 hours" — the window phrase after a usage count. Empty for standing limits. */
export function periodWindow(period: LimitPeriod): string {
  switch (period) {
    case 'rolling_24h':
      return 'in the last 24 hours';
    case 'calendar_day':
      return 'today';
    case 'calendar_month':
      return 'this calendar month';
    case 'standing':
      return '';
  }
}

const join = (...parts: Array<string | undefined | false>) => parts.filter(Boolean).join(' ');

/** Display name of a plan key ('free' → 'Free', 'pro_individual' → 'Pro'). Unknown keys pass through. */
export function planDisplayName(planKey: string | null | undefined): string {
  switch (planKey) {
    case undefined:
    case null:
    case '':
    case 'free':
      return 'Free';
    case 'pro':
    case 'pro_individual':
      return 'Pro';
    case 'pro_ai':
      return 'Pro + AI';
    default:
      return planKey;
  }
}

/**
 * The note shown with an organization-wide limit when the viewer cannot see
 * the workspace's count: the number is the workspace's, not theirs.
 */
export function orgLimitScopeNote(key: PlanLimitKey): string {
  return `This limit is per workspace: it counts the whole workspace's ${LIMIT_COPY[key].noun}, not just yours.`;
}

/** "Practice sessions: 3 per day (rolling 24 hours)" / "Active training modules: 10" / "AI voice minutes: not included". */
export function limitSummary(key: PlanLimitKey, max: number): string {
  const c = LIMIT_COPY[key];
  if (max === 0) return `${c.title}: not included`;
  return `${c.title}: ${join(String(max), periodRate(c.period))}`;
}

/**
 * Summary plus what counts, what does not, and the separate limit it is easy
 * to confuse with (when its number is given in `related`).
 */
export function limitExplanation(
  key: PlanLimitKey,
  max: number,
  related: Partial<Record<PlanLimitKey, number | undefined>> = {}
): string {
  const c = LIMIT_COPY[key];
  const sep = c.separateLimit;
  const sepMax = sep ? related[sep] : undefined;
  const sepRate = sep && sepMax !== undefined ? limitSummary(sep, sepMax).slice(LIMIT_COPY[sep].title.length + 2) : '';
  return join(
    `${limitSummary(key, max)}.`,
    c.counts,
    c.doesNotCount,
    sep &&
      `${LIMIT_COPY[sep].title} have their own limit${sepRate ? `: ${sepRate}` : ''}; starting one is checked only against that limit.`
  );
}

/**
 * The refusal for PREPARING an Interview Prep practice (the extraction LLM call)
 * when the practice-session limit is reached. Preparing is admitted by that
 * limit (interviewPrepController.refuseUnadmittedPrepare →
 * sessionStartPlanLimit), so the copy says exactly that: what it is checked
 * against, what that limit counts, and that preparing itself takes no place.
 * It does NOT claim Interview Prep conversations are counted, nor offer the
 * generic "coaching conversations don't count" line for this request.
 */
export function interviewPrepPrepareLimitMessage(input: {
  key: 'maxDailyPracticeSessions' | 'maxMonthlyPracticeSessions';
  max: number;
  current: number;
  planName: string;
}): string {
  const c = LIMIT_COPY[input.key];
  return join(
    `Preparing an interview practice is checked against your practice-session limit, and you've used ${input.current} of ${input.max} ${c.noun} ${periodWindow(c.period)} on the ${input.planName} plan.`,
    'AI Voice Coach, scenario and training-assignment sessions use that limit.',
    "Preparing doesn't use a place itself, and Interview Prep conversations and NPC Practice battles don't count toward it.",
    'Nothing was prepared or counted.',
    input.key === 'maxDailyPracticeSessions'
      ? 'You can prepare again once a place frees up in the rolling 24 hours, or upgrade for more.'
      : 'You can prepare again next calendar month, or upgrade for more.'
  );
}

export interface LimitReachedInput {
  key: PlanLimitKey;
  max: number;
  current: number;
  /** Plan display name (see planDisplayName / getOrgPlanDisplayName). */
  planName: string;
  related?: Partial<Record<PlanLimitKey, number | undefined>>;
  /**
   * maxTrainingModules only: what the refused request was. Both creating and
   * restoring a module make one more module active, so both need capacity.
   */
  action?: 'create' | 'restore';
}

/** What frees capacity under the active-module cap, by refused request. */
export const TRAINING_MODULE_CAPACITY_ADVICE: Record<'create' | 'restore', string> = {
  create:
    "Archived modules don't count toward this limit: archive a module to free capacity, or upgrade your organization's plan.",
  restore:
    "Archived modules don't count toward this limit: archive another module to free capacity before restoring this one, or upgrade your organization's plan.",
};

/**
 * The refusal message for a plan limit, e.g.
 * "You've used 3 of 3 practice sessions in the last 24 hours on the Free plan. …"
 */
export function limitReachedMessage({ key, max, current, planName, related, action }: LimitReachedInput): string {
  const c = LIMIT_COPY[key];
  const org = c.scope === 'org';
  if (max === 0) {
    return org
      ? `Your organization's ${planName} plan doesn't include ${c.noun}. Upgrade your organization's plan to use them.`
      : `Your ${planName} plan doesn't include ${c.noun}. Upgrade to use them.`;
  }
  const window = periodWindow(c.period);
  const head = key === 'maxAssignmentsPerMember'
    ? `This member already has ${current} of the ${max} training assignments allowed per member on your organization's ${planName} plan.`
    : org
    ? `Your organization has ${current} of ${max} ${c.noun} allowed on the ${planName} plan.`
    : `You've used ${current} of ${max} ${c.noun}${window ? ` ${window}` : ''} on the ${planName} plan.`;
  if (key === 'maxTrainingModules') {
    // The advice says what counts (archived modules don't) and what frees capacity.
    return join(
      `Your organization has reached its limit of ${max} ${c.noun} on the ${planName} plan (${current} active now).`,
      TRAINING_MODULE_CAPACITY_ADVICE[action ?? 'create']
    );
  }
  const rest = limitExplanation(key, max, related ?? {});
  const tail = org ? "Upgrade your organization's plan to add more." : 'Upgrade to continue.';
  return join(head, rest, tail);
}

/** "Voice analyses: 2 per day (resets at midnight, server time)" for a UsageService free-tier limit. */
export function usageLimitSummary(action: string, period: 'calendar_day' | 'calendar_month', max: number): string {
  const c = usageActionCopy(action);
  if (max === 0) return `${c.title}: not included on the Free plan`;
  return `${c.title}: ${max} ${periodRate(period)}`;
}

/** Refusal message for a UsageService free-tier limit. */
export function usageLimitReachedMessage(
  action: string,
  period: 'calendar_day' | 'calendar_month',
  max: number,
  current: number
): string {
  const c = usageActionCopy(action);
  if (max === 0) return `${c.title} aren't included on the Free plan. Upgrade to use them.`;
  return join(
    `You've used ${current} of ${max} ${c.noun} ${periodWindow(period)} on the Free plan.`,
    `${usageLimitSummary(action, period, max)}.`,
    c.counts,
    c.doesNotCount,
    'Upgrade for more.'
  );
}

/**
 * An every-plan daily SAFETY cap was reached (types/usage.ts safetyLimitPerDay):
 * not a plan limit, so no upgrade is offered.
 */
export function usageSafetyLimitReachedMessage(action: string, max: number): string {
  const c = usageActionCopy(action);
  return join(
    `You've used ${max} ${c.noun} today, the most allowed in a day on any plan.`,
    'Nothing was processed or counted. You can continue tomorrow.',
    c.counts,
    c.doesNotCount
  );
}

/**
 * The monthly safety pools (types/usage.ts monthlyAllowance) — a LOCAL
 * NONCOMMERCIAL SAFETY LIMIT, worded as a usage limit, never as credits.
 */
export const ALLOWANCE_POOL_COPY: Record<'practice' | 'coaching' | 'listen', { title: string; noun: string }> = {
  practice: { title: 'Monthly practice allowance', noun: 'practice allowance' },
  coaching: { title: 'Monthly AI voice coaching allowance', noun: 'AI voice coaching allowance' },
  listen: { title: 'Monthly listening allowance', noun: 'listening allowance' },
};

/** Refusal message when a monthly safety pool is used up. */
export function monthlyAllowanceReachedMessage(pool: 'practice' | 'coaching' | 'listen'): string {
  return `You've used this month's ${ALLOWANCE_POOL_COPY[pool].noun}. It resets at the start of next month (server time).`;
}

/** Copy for a UsageService action, with a readable fallback for actions not listed. */
export function usageActionCopy(action: string): UsageActionCopy {
  return (
    USAGE_ACTION_COPY[action] ?? {
      title: action.replace(/_/g, ' ').replace(/^\w/, (s) => s.toUpperCase()),
      noun: action.replace(/_/g, ' '),
      counts: 'Each use counts.',
    }
  );
}

/** Lower-case labels keyed by limit key (the shape LIMIT_KEY_LABELS always had). */
export function limitLabels(scope?: 'user' | 'org'): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, c] of Object.entries(LIMIT_COPY)) {
    if (scope && c.scope !== scope) continue;
    const rate = c.period === 'rolling_24h' ? 'daily ' : c.period === 'calendar_month' ? 'monthly ' : '';
    out[key] = `${rate}${c.noun}`;
  }
  return out;
}

/**
 * The usage / plan-capacity authority could not be consulted (a database or
 * service failure inside an admission check) — NOT a limit. The API answers
 * 503 with this code, refusing before any provider call, write or usage metering,
 * so retrying is safe. Never worded as "limit reached": nothing was used up.
 */
export const USAGE_AUTHORITY_UNAVAILABLE_CODE = 'USAGE_AUTHORITY_UNAVAILABLE';
export const USAGE_AUTHORITY_UNAVAILABLE_MESSAGE =
  "We couldn't check your plan's usage just now, so nothing was processed and nothing was counted. This is temporary — please try again in a moment.";

// ── Conversation safety caps (noncommercial) ────────────────────────────────
//
// Not plan limits: the same generous numbers for every plan, not sold and not
// shown on pricing (config/conversationSafety.ts). They bound conversations
// while the commercial credit reservation is off, and every conversation's
// turn count always. The number is passed in, like every limit here.

/** Which conversation safety cap was reached. */
export type ConversationSafetyLimit =
  | 'daily_conversation_starts'
  | 'conversation_user_turns'
  /** Voice turns one practice session may take (POST /voice/practice/sessions/:id/turn). */
  | 'practice_session_turns';

export const CONVERSATION_SAFETY_LIMIT_CODE = 'CONVERSATION_SAFETY_LIMIT';

/** The refusal for a conversation safety cap. Nothing was processed or counted. */
export function conversationSafetyLimitMessage(limit: ConversationSafetyLimit, max: number): string {
  if (limit === 'practice_session_turns') {
    return (
      `This practice session has reached ${max} recordings, the most one session can hold on any plan. ` +
      'Nothing was processed or counted. Finish this session and start a new one to keep practising.'
    );
  }
  return limit === 'daily_conversation_starts'
    ? `You've started ${max} conversations in the last 24 hours, the most allowed in that time on any plan. ` +
        'Nothing new was started. You can start another once your earliest one is more than 24 hours old.'
    : `This conversation has reached ${max} of your messages, the most one conversation can hold on any plan. ` +
        'Your message was not sent. Start a new conversation to keep practising.';
}
